import { useState, useEffect } from 'react';
import { toast } from '@/hooks/ui/use-toast';
import { supabase as supabaseClient } from '@/integrations/supabase/client';
import { Employee } from '@/types';
// Removed cache service

type EmployeeDataLevel = 'base' | 'standard' | 'communication' | 'advanced';

interface UseEmployeesProps {
  businessId: string | null;
  dataLevel?: EmployeeDataLevel; // Controls how much data to fetch
  includeRoles?: boolean; // Legacy: for backward compatibility
  includeChildren?: boolean; // Legacy: for backward compatibility
}

interface UseEmployeesReturn {
  employees: Employee[];
  loadingEmployees: boolean;
  loading: boolean; // Alternative name for compatibility
  refetch: () => void;
  invalidateCache: () => void; // New: for cache invalidation when data changes
}

/**
 * Core hook for fetching employees by business ID with smart field selection
 * Provides employee data for general use across components
 * Now supports different data levels to avoid fetching unnecessary data
 */
export const useEmployees = (
  // Support both object and direct parameter formats for backward compatibility
  params: UseEmployeesProps | string | null
): UseEmployeesReturn => {
  // Handle both parameter formats: useEmployees({ businessId }) and useEmployees(businessId)
  const isObjectParam = typeof params === 'object' && params !== null;
  const businessId = isObjectParam ? params.businessId : params;
  const dataLevel = isObjectParam ? (params.dataLevel || 'advanced') : 'advanced'; // Default to advanced for backward compatibility
  const includeRoles = isObjectParam ? params.includeRoles : undefined;
  const includeChildren = isObjectParam ? params.includeChildren : undefined;
  
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);

  // Build SQL query based on data level for smart field selection
  const buildSelectQuery = (level: EmployeeDataLevel): string => {
    switch (level) {
      case 'base':
        return 'id, name, email';
      case 'communication':
        return `
          id,
          name,
          email,
          role_id,
          user_id,
          created_at,
          roles(name)
        `;
      case 'standard':
        return `
          id,
          business_id,
          name,
          email,
          role_id,
          employment_type_id,
          contract_type,
          employment_unified_type,
          contract_start_date,
          contract_end_date,
          hourly_rate,
          salary_adjustment,
          roleInfo:roles(name, default_pay_rate, pay_type, hours_per_month),
          employmentType:employment_types(id, code, label_native, label_en, category)
        `;
      case 'advanced':
      default:
        // Full data - for backward compatibility and when all data is needed
        return `
          *,
          roleInfo:roles(id, name, default_pay_rate, pay_type, hours_per_month, business_id, created_at, updated_at),
          employmentType:employment_types(id, code, label_native, label_en, category, max_weekly_hours, max_yearly_hours),
          children:employee_children(*),
          garnishments:employee_garnishments(*)
        `;
    }
  };

  const fetchEmployees = async () => {
    if (!businessId) {
      setEmployees([]);
      return;
    }

    setLoading(true);

    try {
      const selectQuery = buildSelectQuery(dataLevel);

      // Direct database call without caching
      const { data, error } = await supabaseClient
        .from('employees')
        .select(selectQuery)
        .eq('business_id', businessId);

      if (error) {
        throw error;
      }

      const employees = data as Employee[] || [];
      setEmployees(employees);

    } catch (error: unknown) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to fetch employee data', {
        description: error instanceof Error ? error.message : String(error)
      });
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [businessId, dataLevel]);

  // Helper function to refetch data when employee data changes
  const invalidateCache = () => {
    // No cache to invalidate - just refetch data
    fetchEmployees();
  };

  return {
    employees,
    loadingEmployees: loading,
    loading, // Alternative name for compatibility
    refetch: fetchEmployees,
    invalidateCache, // Refetches data when employee data changes
  };
};